import React from 'react'

import * as c from '../../../common'
import * as r from '../../../react-utils'

import Button from '@mui/material/Button'
import moment from 'moment'

import * as gs from '../../../app/gStyles'
import { nirvanaContract } from '../../constants'

import ContractDetailRow, {
  ContractDetailRowProps as IContractDetailRow,
} from './ContractDetailRow'
import ContractLogo from './ContractLogo'
import ReportPages, { horizontalPad } from './ReportPages'
//@ts-ignore
import styles from './NirvanaContract.module.css'

export type NirvanaContractProps = c.r<{
  customerID: string
  isPDF: boolean
  toggleCompanySignatureModal(): void
  toggleSignatureModal(): void
}>

export default React.memo<NirvanaContractProps>(function NirvanaContract({
  customerID,
  isPDF,
  toggleCompanySignatureModal,
  toggleSignatureModal,
}) {
  //#region styling

  //#endregion
  //#region global
  const { contractCompanyFull } = r.useContractCompany(customerID)
  const strings = React.useMemo(
    () =>
      nirvanaContract(
        contractCompanyFull.longName,
        contractCompanyFull.shortName,
      ),
    [contractCompanyFull.longName, contractCompanyFull.shortName],
  )
  const totalCost = r.useTotalCost('customer', customerID)

  const [customerData] = r.useCustomer(customerID)

  const {
    c_signature,
    c_signature_date,
    customerAddress,
    customerEmail,
    customerName,
    customerPhone,
    customerPhoneAlt,
    homeRep,
    report_company_signature,
    report_company_signature_date,
    solarCompany,
  } = customerData
  //#endregion
  //#region local
  //
  const [finance, , financeHandle] = r.useCustomerFieldInput(
    customerID,
    'report_nirvana_finance',
  )
  const [upfront, , upfrontHandle] = r.useCustomerFieldInput(
    customerID,
    'report_nirvana_upfront',
  )
  const [remaining, , remainingHandle] = r.useCustomerFieldInput(
    customerID,
    'report_nirvana_remaining',
  )
  //#endregion

  const buttonSignatureStyle = React.useMemo(
    () => ({
      backgroundColor: c.companyColors[solarCompany],
      '&:hover': { backgroundColor: c.companyColors[solarCompany] },
      width: 256,
    }),
    [solarCompany],
  )

  const _rows = React.useMemo((): readonly IContractDetailRow[] => {
    return buildContractRows(customerData)
  }, [customerData])
  const lastRow = _rows[_rows.length - 1]
  const rows = _rows.slice(0, -1)

  return (
    <ReportPages companyBorder customerID={customerID} hPad>
      <div style={sx.logo}>
        <ContractLogo isPDF={isPDF} size="l" />
      </div>

      {gapBig}

      <div>
        <p className={styles['heading']}>{contractCompanyFull.longName}</p>

        {gapBig}

        <p style={sx.companyInfo}>{contractCompanyFull.address1}</p>
        <p style={sx.companyInfo}>{contractCompanyFull.email}</p>
        <p style={sx.companyInfo}>{contractCompanyFull.phoneNumber}</p>
      </div>

      {gapMedium}

      <p style={sx.node}>
        Date:
        <span className={styles['underlined']}>
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          {c_signature
            ? moment(c_signature_date).format(c.readableDateFormat)
            : moment().format(c.readableDateFormat)}
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
        </span>
        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; Property Owner(s):
        <span className={styles['underlined']}>
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          {customerName}
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
        </span>
      </p>

      <p style={sx.node}>
        <span>Phone: </span>
        <span className={styles['underlined']}>
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          {customerPhone}
          {customerPhoneAlt ? ` / ${customerPhoneAlt}` : ''}
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
        </span>
      </p>

      <p style={sx.node}>
        <span>Email: </span>
        <span className={styles['underlined']}>
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          {customerEmail}
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
        </span>
      </p>

      {gapSmall}

      <p style={sx.node}>
        Property Address:
        <span className={styles['underlined']}>
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          {customerAddress}
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
        </span>
      </p>

      {gapBig}

      <p style={sx.node}>
        {strings['aa']}{' '}
        <span className={styles['underlined']}>
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          {`$ ${totalCost}`}
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
        </span>
        {strings['ab']}
      </p>

      {gapBig}

      <p className={styles['subheading']}>System Information:</p>

      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          fontFamily: 'Game',
          margin: 0,
          width: '100%',
        }}
      >
        <p
          style={{
            fontFamily: 'Game',
            fontWeight: 'bold',
            textAlign: 'center',
            width: '15%',
          }}
        >
          Quantity
        </p>

        <p style={{ fontFamily: 'Game', fontWeight: 'bold', width: '55%' }}>
          Description
        </p>

        <p
          style={{
            fontFamily: 'Game',
            fontWeight: 'bold',
            textAlign: 'end',
            width: '15%',
          }}
        >
          Unit Cost
        </p>

        <p
          style={{
            fontFamily: 'Game',
            fontWeight: 'bold',
            textAlign: 'end',
            width: '15%',
          }}
        >
          Total Cost
        </p>
      </div>

      {rows.map((row) => (
        <ContractDetailRow {...row} key={row.description} />
      ))}

      <div style={gs.flowRootWidth100}>
        {lastRow && <ContractDetailRow {...lastRow} />}

        <p style={sx.grandTotal}>
          <span style={{ fontFamily: 'Game', fontWeight: 'bold' }}>
            GRAND TOTAL: {c.blankChar(12)}
            {Intl.NumberFormat('en-US', {
              currency: 'USD',
              maximumFractionDigits: 2,
              minimumFractionDigits: 2,
              style: 'currency',
            }).format(totalCost)}
          </span>
        </p>
      </div>

      {gapBig}

      {/* This flow-root ensures cTitle doesn't get spilt off its content and
      also ensures a page break after the scope of work unless the scope of work
      is really small, overall making it more presentable */}
      <div style={gs.flowRootWidth100}>
        <p className={styles['subheading']}>{strings['cTitle']}</p>

        {gapSmall}

        {/* Have total amt floating around somewhere in here */}
        <p style={sx.node}>
          <span>{strings['ca']} </span>

          <input
            className={styles['input-inline']}
            onChange={financeHandle}
            title={`Total cost (pre-financing): ${totalCost}`}
            value={finance}
          />
        </p>

        <p style={sx.node}>
          <span>{strings['cb']}</span>

          <input
            className={styles['input-inline']}
            onChange={upfrontHandle}
            title={`Total cost (pre-financing): ${totalCost}`}
            value={upfront}
          />

          <span>{strings['cc']}</span>

          <input
            className={styles['input-inline']}
            onChange={remainingHandle}
            title={`Total cost (pre-financing): ${totalCost}`}
            value={remaining}
          />

          <span>{strings['cd']}</span>
        </p>

        {gapBig}

        <p className={styles['subheading']}>{strings['dTitle']}</p>

        {gapSmall}

        <p>{strings['da']}</p>

        {gapBig}
      </div>
      {/* end flow-root */}

      <p>{strings['ea']}</p>

      {gapBig}

      <p>{strings['fa']}</p>

      {gapBig}

      <p>{strings['ga']}</p>

      {gapBig}

      <p>{strings['ha']}</p>

      {gapBig}

      <p>{strings['ia']}</p>

      {gapBig}

      <p>{strings['ja']}</p>

      {gapBig}

      <p>{strings['ka']}</p>

      {gapBig}

      <p>{strings['la']}</p>

      {gapBig}

      {/* This flow-root ensures signatures aren't awkwardly by themselves on
        a lone page, tying them together with the last paragraph of 
        conditions */}
      <div style={gs.flowRootWidth100}>
        <p>{strings['ma']}</p>

        {gapBig}

        <div className={styles['signatures']}>
          <div className={styles['signature-container']}>
            <div>
              <p className={styles['label-signature']}>Property owner:</p>

              <span>{customerName}</span>
            </div>

            {gs.rowGap24}

            {c_signature && (
              <>
                <img
                  alt=""
                  className={styles['signature-img']}
                  src={c_signature}
                  width={200}
                />
                <p>{moment(c_signature_date).format('YYYY/MM/DD - hh:mma')}</p>
              </>
            )}

            <p>Address: {customerAddress}</p>

            {gs.rowGap24}

            {isPDF || c_signature ? (
              <div />
            ) : (
              <Button
                onClick={toggleSignatureModal}
                sx={buttonSignatureStyle}
                variant="contained"
              >
                Client Signature
              </Button>
            )}
          </div>

          <div className={styles['signature-container']}>
            <div>
              <p className={styles['label-signature']}>Contractor:</p>
              <span>{homeRep}</span>
            </div>

            {report_company_signature && (
              <>
                <img
                  alt=""
                  className={styles['signature-img']}
                  src={report_company_signature}
                  width={200}
                />
                <p>
                  {moment(report_company_signature_date).format(
                    'YYYY/MM/DD - hh:mma',
                  )}
                </p>
              </>
            )}

            {gs.rowGap24}

            <p className={styles['label-signature']}>
              Authorized Representative of
            </p>
            <p className={styles['label-signature']}>
              {contractCompanyFull.longName}
            </p>

            {gs.rowGap24}

            {isPDF || report_company_signature ? (
              <div />
            ) : (
              <Button
                onClick={toggleCompanySignatureModal}
                sx={buttonSignatureStyle}
                variant="contained"
              >
                Authorized Signature
              </Button>
            )}
          </div>
        </div>
      </div>
    </ReportPages>
  )
})

const gapBig = gs.rowGap32
const gapMedium = gs.rowGap28
const gapSmall = gs.rowGap16

const sx = {
  companyInfo: { margin: 0, textAlign: 'center' as const },
  grandTotal: {
    fontFamily: 'Game',
    fontSize: 18,
    margin: 0,
    textAlign: 'end' as const,
    width: '100%',
  },
  logo: { left: horizontalPad, position: 'absolute' as const, top: '96px' },
  node: { alignSelf: 'flex-start', margin: 0 },
}

export const buildContractRows = (
  customerData: c.Customer,
): readonly IContractDetailRow[] => {
  return c.efficiencyKeys
    .filter((k) => customerData[k] === 'yes')
    .map((k): IContractDetailRow => {
      const isCustom = k.startsWith('custom_')
      const description = isCustom
        ? (customerData[(k + '_name') as c.CustomerField] as string)
        : c.getFieldLabel(k)
      const costCustomer = c.parseFloating(
        customerData[(k + '_customer_cost') as c.CustomerField],
      )
      const qty =
        c.parseInt(
          (customerData[(k + '_qty') as c.CustomerField] as
            | string
            | undefined) ||
            (customerData[(k + '_how_many') as c.CustomerField] as
              | string
              | undefined),
        ) || -1

      return {
        description,
        notes: c.emptyArrStr,
        price: costCustomer / (qty === -1 ? 1 : qty),
        qty,
      }
    })
}
